import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

type ToastTone = 'success' | 'error' | 'info';

type ToastItem = {
  id: number;
  title?: string;
  message: string;
  tone: ToastTone;
  leaving: boolean;
};

type ToastOptions = { title?: string; tone?: ToastTone };
type ToastContextValue = { showToast: (message: string, options?: ToastOptions) => void };

const ToastContext = createContext<ToastContextValue | null>(null);

const DURATION = 4000;
const EXIT_MS = 260;
let _id = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const t = timers.current;
    return () => t.forEach((id) => clearTimeout(id));
  }, []);

  const dismiss = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    const exitTimer = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      timers.current.delete(-id);
    }, EXIT_MS);
    timers.current.set(-id, exitTimer);
  }, []);

  const showToast = useCallback(
    (message: string, options?: ToastOptions) => {
      const id = ++_id;
      setToasts((prev) => [
        ...prev,
        { id, message, title: options?.title, tone: options?.tone ?? 'success', leaving: false },
      ]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), DURATION),
      );
    },
    [dismiss],
  );

  const portal = createPortal(
    <div id="toast-container" aria-live="polite" aria-atomic="false">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={['toast', `toast-${t.tone}`, t.leaving ? 'toast-leaving' : '']
            .filter(Boolean)
            .join(' ')}
          role="status"
          onClick={() => dismiss(t.id)}
        >
          <span className="toast-icon" aria-hidden="true">
            <ToastIcon tone={t.tone} />
          </span>
          <span className="toast-message">
            {t.title ? <strong className="toast-title">{t.title}</strong> : null}
            <span className="toast-body">{t.message}</span>
          </span>
        </div>
      ))}
    </div>,
    document.body,
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {portal}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

function ToastIcon({ tone }: { tone: ToastTone }) {
  if (tone === 'success') {
    return (
      <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="2.5,8.5 6,12 13.5,4" />
      </svg>
    );
  }
  if (tone === 'error') {
    return (
      <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <circle cx="8" cy="8" r="6" />
        <line x1="8" y1="5" x2="8" y2="8.5" />
        <line x1="8" y1="11" x2="8" y2="11.1" />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <circle cx="8" cy="8" r="6" />
      <line x1="8" y1="8" x2="8" y2="11" />
      <line x1="8" y1="5" x2="8" y2="5.1" />
    </svg>
  );
}
